import { useState, useEffect } from 'react';
import { Image as ImageIcon, Upload, Trash2, ZoomIn, X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export default function ProjectImagesCard() {
  const {
    projectImages,
    setProjectImages,
    isDragging,
    setIsDragging,
    projectImageInputRef,
    handleProjectImagesDrop,
    readOnlyState,
    proposalType,
    units,
    activeUnitId,
  } = useAppContext();

  const activeUnitLabel = proposalType === 'bulk' ? units.find(u => u.id === activeUnitId)?.label : undefined;

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxIndex(null);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex(prev => (prev !== null && prev > 0 ? prev - 1 : projectImages.length - 1));
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex(prev => (prev !== null && prev < projectImages.length - 1 ? prev + 1 : 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, projectImages.length]);

  const handleDownloadImage = (imgUrl: string, index: number) => {
    const link = document.createElement('a');
    link.href = imgUrl;
    link.download = `Proje_Gorseli_${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div 
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); handleProjectImagesDrop(e); }}
      className={`bg-white p-6 rounded-xl border transition-all space-y-4 shadow-card ${
        isDragging 
          ? 'border-brand-500 bg-brand-50/10 ring-2 ring-brand-500/20' 
          : 'border-stone-200/80'
      }`}
    >
      <div className="flex items-center justify-between border-b border-stone-100 pb-3">
        <div className="flex items-center space-x-2 text-stone-800 font-semibold">
          <ImageIcon className="w-4 h-4 text-brand-600" />
          <span className="text-xs uppercase tracking-wider font-semibold text-stone-700">Proje Görselleri</span>
          {activeUnitLabel && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50/80 border border-brand-200/60 px-2.5 py-0.5 rounded-full">
              {activeUnitLabel}
            </span>
          )}
        </div>
        {!readOnlyState?.isReadOnly && (
          <button
            type="button"
            onClick={() => projectImageInputRef.current?.click()}
            className="bg-brand-50/80 hover:bg-brand-100 text-brand-700 border border-brand-200/60 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-all"
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Görsel Yükle</span>
          </button>
        )}
      </div>

      <p className="text-[11px] text-stone-500 leading-relaxed">
        Mutfak projesine ait yatay (geniş) render veya teknik çizim görsellerini yükleyin. Görselin üzerine tıklayarak büyük haliyle inceleyebilirsiniz.
      </p>

      {projectImages.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
          {projectImages.map((imgUrl, index) => (
            <div 
              key={index} 
              onClick={() => setLightboxIndex(index)}
              className="relative border border-stone-200 rounded-lg overflow-hidden aspect-video bg-stone-50 group cursor-pointer shadow-xs hover:border-brand-400 transition-all"
            >
              <img 
                src={imgUrl} 
                alt={`Proje Görseli ${index + 1}`} 
                className="w-full h-full object-contain" 
              />
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center space-x-2 transition-all">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(index);
                  }}
                  className="bg-white/90 hover:bg-white text-stone-800 p-2 rounded-full shadow-md hover:scale-105 transition-all"
                  title="Görseli Büyüt & İncele"
                >
                  <ZoomIn className="w-4 h-4 text-stone-900" />
                </button>

                {!readOnlyState?.isReadOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProjectImages(projectImages.filter((_, i) => i !== index));
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-md hover:scale-105 transition-all"
                    title="Görseli sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <span className="absolute bottom-1.5 left-1.5 bg-stone-900/80 text-white text-[9px] px-2 py-0.5 rounded-md font-bold tracking-tight shadow-xs">
                Görsel {index + 1}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div 
          onClick={() => !readOnlyState?.isReadOnly && projectImageInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
            isDragging 
              ? 'border-brand-500 bg-brand-50/20 text-brand-700' 
              : 'border-stone-200 hover:border-brand-400 hover:bg-stone-50/50 text-stone-400 hover:text-brand-600'
          }`}
        >
          <Upload className={`w-8 h-8 mx-auto mb-2 transition-all ${isDragging ? 'text-brand-500 scale-110' : 'text-stone-300'}`} />
          <span className="text-xs font-semibold block">Görselleri Seçmek Veya Sürüklemek İçin Tıklayın</span>
          <span className="text-[9px] text-stone-400 block mt-1">Birden fazla görsel seçebilir ya da buraya sürükleyebilirsiniz</span>
        </div>
      )}

      {/* Full-Screen Lightbox Modal */}
      {lightboxIndex !== null && (
        <div 
          onClick={() => setLightboxIndex(null)}
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200 no-print select-none"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center"
          >
            {/* Top Toolbar */}
            <div className="w-full flex items-center justify-between text-white pb-3 border-b border-white/10 mb-3 px-2">
              <div className="flex items-center space-x-2">
                <ImageIcon className="w-4 h-4 text-brand-400" />
                <span className="font-bold text-xs tracking-wider">
                  Proje Görseli {lightboxIndex + 1} / {projectImages.length}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => handleDownloadImage(projectImages[lightboxIndex], lightboxIndex)}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center space-x-1 text-xs font-semibold"
                  title="Görseli İndir"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">İndir</span>
                </button>

                <button
                  type="button"
                  onClick={() => setLightboxIndex(null)}
                  className="p-2 bg-white/10 hover:bg-red-600/80 text-white rounded-lg transition-colors"
                  title="Kapat (ESC)"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Main Image View */}
            <div className="relative flex items-center justify-center w-full max-h-[75vh] overflow-hidden">
              <img 
                src={projectImages[lightboxIndex]} 
                alt={`Büyük Proje Görseli ${lightboxIndex + 1}`}
                className="max-h-[75vh] max-w-full object-contain rounded-xl shadow-2xl border border-stone-800 animate-in zoom-in-95 duration-200"
              />

              {/* Prev Button */}
              {projectImages.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(lightboxIndex > 0 ? lightboxIndex - 1 : projectImages.length - 1);
                  }}
                  className="absolute left-2 p-3 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all border border-white/20 hover:scale-110"
                  title="Önceki Görsel (Sol Ok)"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {/* Next Button */}
              {projectImages.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(lightboxIndex < projectImages.length - 1 ? lightboxIndex + 1 : 0);
                  }}
                  className="absolute right-2 p-3 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all border border-white/20 hover:scale-110"
                  title="Sonraki Görsel (Sağ Ok)"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* Footer Navigation Dots / Hint */}
            <div className="mt-4 flex items-center justify-center space-x-2">
              {projectImages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    i === lightboxIndex ? 'bg-brand-500 w-6' : 'bg-white/30 hover:bg-white/60'
                  }`}
                  title={`Görsel ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
